import React, { useEffect, useRef, useState } from 'react';

import {
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import PositionPill from './PositionPill';
import { UI, cardSx, moneyShort, tableSx, value } from './operationalUi';
import type { OperationalRollup, OperationalRow } from './types';

export type AttentionRow = OperationalRow;
export type RollupRow = OperationalRollup;
interface Props {
  rows: AttentionRow[];
  rollups: { territory: RollupRow[]; pcm: RollupRow[] };
  mode: string;
  onMode: (mode: string) => void;
  onCampaign: (id: string) => void;
  onRollup: (key: string) => void;
  onUncovered: () => void;
  uncovered: number;
}
export function ownership(row: AttentionRow) {
  return row.pcm && row.pcm !== 'No current-quarter PCM record'
    ? row.pcm
    : row.ownerSalesforce
      ? `Owner (Salesforce): ${row.ownerSalesforce}`
      : 'No current-quarter PCM record';
}
export function rowPill(row: AttentionRow) {
  return (
    <PositionPill
      compact
      position={row.positions?.margin_per_day}
      level={row.level}
      n={row.n}
      label={
        row.tooEarly
          ? `${row.daysObserved} observed days, too early`
          : row.zeroSale
            ? `No sales recorded in ${row.daysObserved} days, ${value(row.totals.views)} views`
            : !row.comparable
              ? 'No peer group yet'
              : undefined
      }
      reason={row.reason}
    />
  );
}
export const stepLabel = (id: string | null) =>
  ({
    impressions_to_views: 'Impressions → views',
    views_to_packages: 'Views → packages',
    margin_per_view: 'Margin / view',
  })[id || ''] || '—';
export default function AttentionList({
  rows,
  rollups,
  mode,
  onMode,
  onCampaign,
  onRollup,
  onUncovered,
  uncovered,
}: Props) {
  const groups = mode === 'pcm' ? rollups.pcm : rollups.territory;
  const viewport = useRef<HTMLDivElement>(null),
    table = useRef<HTMLTableElement>(null),
    [tableHeight, setTableHeight] = useState<number>();
  useEffect(() => {
    const fit = () => {
      if (!viewport.current || !table.current) return;
      const available = viewport.current.clientHeight;
      let height = table.current.querySelector('thead')?.getBoundingClientRect().height || 32;
      for (const row of Array.from(table.current.querySelectorAll('tbody tr'))) {
        const next = row.getBoundingClientRect().height;
        if (height + next > available + 0.5) break;
        height += next;
      }
      setTableHeight(Math.min(available, Math.floor(height)));
    };
    fit();
    const observer = new ResizeObserver(fit);
    if (viewport.current) observer.observe(viewport.current);
    return () => observer.disconnect();
  }, [mode, rows, groups]);
  return (
    <Box sx={{ ...cardSx, p: 1.5, height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: UI.teal, letterSpacing: 0.6 }}>
          {mode === 'campaign' ? 'CAMPAIGNS TO REVIEW' : 'PERFORMANCE BY GROUP'}
        </Typography>
        <ToggleButtonGroup
          value={mode}
          exclusive
          size="small"
          onChange={(_, v) => v && onMode(v)}
          aria-label="Attention roll-up"
          sx={{ '& .MuiToggleButton-root': { py: 0.5, fontSize: 12 } }}
        >
          <ToggleButton value="campaign">Campaign</ToggleButton>
          <ToggleButton value="territory">Territory</ToggleButton>
          <ToggleButton value="pcm">PCM</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <Typography sx={{ fontSize: 12, color: UI.muted, py: 1 }}>
        {mode === 'campaign' ? 'Below peers · 7+ days · largest rate gaps first.' : 'Sorted by total margin to date.'}
      </Typography>
      <Box ref={viewport} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <TableContainer sx={{ height: tableHeight || '100%', overflow: 'auto', borderBottom: `1px solid ${UI.line}` }}>
          <Table
            ref={table}
            stickyHeader
            size="small"
            sx={{
              ...tableSx,
              '& .MuiTableCell-root': { fontSize: 13, py: 0.5, px: 0.75, borderColor: UI.line, lineHeight: 1.25 },
              tableLayout: 'fixed',
              '@container (max-width:600px)': { minWidth: 620 },
            }}
            aria-label={mode === 'campaign' ? 'Campaigns to look at' : `${mode} roll-up`}
          >
            <TableHead>
              <TableRow>
                {(mode === 'campaign'
                  ? ['#', 'Campaign', 'Day', 'Weakest step', 'Position on margin', 'v peer pace, 28d']
                  : ['Territory / PCM', 'Live', 'With peers', 'Margin to date', 'Position counts', 'Behind peer pace']
                ).map((label, i) => (
                  <TableCell
                    key={label}
                    sx={{
                      width:
                        mode === 'campaign'
                          ? ['4%', '24%', '10%', '18%', '25%', '19%'][i]
                          : ['24%', '8%', '16%', '17%', '18%', '17%'][i],
                      whiteSpace: 'normal!important',
                    }}
                  >
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {mode === 'campaign'
                ? rows.slice(0, 15).map((row, i) => (
                    <TableRow
                      hover
                      key={row.id}
                      onClick={() => onCampaign(row.id)}
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && onCampaign(row.id)}
                      sx={{
                        cursor: 'pointer',
                        height: 44,
                        bgcolor: row.zeroSale ? UI.below.background : undefined,
                        borderLeft: row.zeroSale ? `3px solid ${UI.below.color}` : undefined,
                      }}
                    >
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={row.name}
                        >
                          {row.name}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: UI.muted,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={ownership(row)}
                        >
                          {row.destination} · {ownership(row)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {row.ageDays} of {row.durationDays}
                        <Typography sx={{ fontSize: 12, color: UI.muted }}>{row.daysLeft}d left</Typography>
                      </TableCell>
                      <TableCell>{stepLabel(row.weakestStep)}</TableCell>
                      <TableCell>{rowPill(row)}</TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: !row.tooEarly && row.positions.margin_per_day === 'below' ? UI.below.color : UI.muted,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {value(row.gap, 'currency')} ›
                      </TableCell>
                    </TableRow>
                  ))
                : groups.map((row) => (
                    <TableRow
                      hover
                      key={row.key}
                      onClick={() => onRollup(row.key)}
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && onRollup(row.key)}
                      sx={{ cursor: 'pointer', height: 62 }}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>{row.key}</TableCell>
                      <TableCell>{row.nLive}</TableCell>
                      <TableCell>
                        {row.nComparable} of {row.nLive}
                        <Typography sx={{ fontSize: 12, color: UI.muted }}>{row.nOnCountryPeers} country</Typography>
                      </TableCell>
                      <TableCell>{moneyShort(row.sumMarginToDateAllLive)}</TableCell>
                      <TableCell>
                        {row.nComparable
                          ? `${row.below} below · ${row.inLine} in line · ${row.above} above`
                          : 'No peer group'}
                        {row.tooEarly > 0 && (
                          <Typography sx={{ fontSize: 12, color: UI.muted }}>{row.tooEarly} too early</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">{moneyShort(row.sumBehind)} ›</TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
          {!(mode === 'campaign' ? rows : groups).length && (
            <Typography sx={{ p: 3, color: UI.muted }}>
              {mode === 'campaign'
                ? 'No mature campaigns are below peers in this scope. See All Campaigns for the full portfolio.'
                : 'No campaigns in this group.'}
            </Typography>
          )}
        </TableContainer>
      </Box>
      <Stack direction="row" flexWrap="wrap" justifyContent="space-between" alignItems="center" gap={1} sx={{ pt: 1 }}>
        <Typography sx={{ fontSize: 12, color: UI.muted }}>
          {mode === 'campaign'
            ? `${Math.min(rows.length, 15)} of ${rows.length} review priorities`
            : `${groups.length} ${mode === 'pcm' ? 'PCM' : 'territory'} groups`}
        </Typography>
        {uncovered > 0 && (
          <Button size="small" sx={{ p: 0, minWidth: 0, fontSize: 12 }} onClick={onUncovered}>
            {uncovered} without peers ›
          </Button>
        )}
      </Stack>
    </Box>
  );
}
